<template>
  <Transition name="fade">
    <div v-if="isVisible" class="html-generation-overlay">
      <div class="html-generation-content">
        <div class="loader-animation">
          <div class="spinner" />
          <div class="html-icon">
            🌐
          </div>
        </div>

        <h2 class="loading-title">
          Génération du HTML en cours
        </h2>
        <p class="loading-subtitle">
          Veuillez patienter pendant que nous créons votre fichier HTML...
        </p>

        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progress + '%' }" />
          </div>
          <span class="progress-text">{{ Math.round(progress) }}%</span>
        </div>

        <div class="timer-container">
          <div class="timer-icon">
            ⏱️
          </div>
          <span class="timer-text">{{ formatTime(elapsedTime) }}</span>
        </div>

        <div class="loading-steps">
          <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
            <span class="step-icon">📄</span>
            <span class="step-text">Chargement du document</span>
          </div>
          <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
            <span class="step-icon">🎨</span>
            <span class="step-text">Rendu des styles CSS</span>
          </div>
          <div class="step" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
            <span class="step-icon">🖼️</span>
            <span class="step-text">Capture du contenu</span>
          </div>
          <div class="step" :class="{ active: currentStep >= 4, completed: currentStep > 4 }">
            <span class="step-icon">💾</span>
            <span class="step-text">Génération du fichier</span>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch } from '@vue/runtime-core';

const props = defineProps<{
  isVisible: boolean;
}>();

const progress = ref(0);
const elapsedTime = ref(0);
const currentStep = ref(1);
const isTimedOut = ref(false);

let progressInterval: NodeJS.Timeout | null = null;
let timerInterval: NodeJS.Timeout | null = null;
let timeoutInterval: NodeJS.Timeout | null = null;

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const startProgress = () => {
  progress.value = 0;
  elapsedTime.value = 0;
  currentStep.value = 1;
  isTimedOut.value = false;

  console.log("🔄 HtmlGenerationLoader - Démarrage du progrès");

  // Simuler le progrès
  progressInterval = setInterval(() => {
    if (progress.value < 90) {
      // Progression plus rapide au début, puis ralentit
      const increment = Math.max(1, (90 - progress.value) * 0.1);
      progress.value = Math.min(90, progress.value + increment);

      // Mettre à jour les étapes
      if (progress.value > 20) currentStep.value = 2;
      if (progress.value > 50) currentStep.value = 3;
      if (progress.value > 75) currentStep.value = 4;
    } else if (progress.value < 99) {
      // Progression très lente entre 90% et 99%
      progress.value = Math.min(99, progress.value + 0.2);
    }
    
    console.log(`📊 Progrès: ${Math.round(progress.value)}% - Étape: ${currentStep.value}`);
  }, 200);

  // Timer
  timerInterval = setInterval(() => {
    elapsedTime.value++;
  }, 1000);
  
  // Timeout de sécurité (60 secondes)
  timeoutInterval = setTimeout(() => {
    console.warn("⚠️ HtmlGenerationLoader - TIMEOUT atteint (60s)");
    isTimedOut.value = true;
    progress.value = 100;
    currentStep.value = 5;
    
    // Auto-fermeture après timeout
    setTimeout(() => {
      console.log("🔄 Auto-fermeture après timeout");
      stopProgress();
    }, 2000);
  }, 60000); // 60 secondes
};

const stopProgress = () => {
  console.log("⏹️ HtmlGenerationLoader - Arrêt du progrès");
  
  if (progressInterval) {
    clearInterval(progressInterval);
    progressInterval = null;
  }
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  if (timeoutInterval) {
    clearTimeout(timeoutInterval);
    timeoutInterval = null;
  }

  // Animation de completion (sauf si timeout)
  if (!isTimedOut.value) {
    progress.value = 100;
    currentStep.value = 5;
    console.log("✅ Progrès complété avec succès");
  }
};

// Watcher pour démarrer/arrêter le progrès
watch(() => props.isVisible, (newValue: boolean) => {
  if (newValue) {
    startProgress();
  } else {
    stopProgress();
  }
});

onUnmounted(() => {
  stopProgress();
});
</script>

<style scoped>
.html-generation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: slideIn 0.3s ease-out;
}

.html-generation-content {
  text-align: center;
  max-width: 500px;
  padding: 3rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.loader-animation {
  position: relative;
  margin-bottom: 2rem;
}

.spinner {
  width: 80px;
  height: 80px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

.html-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  animation: pulse 2s infinite;
}

.loading-title {
  font-size: 1.8rem;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.loading-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

.progress-container {
  margin-bottom: 2rem;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #b70018, #e60023);
  border-radius: 4px;
  transition: width 0.3s ease;
  animation: shimmer 2s infinite;
}

.progress-text {
  font-size: 0.9rem;
  color: #374151;
  font-weight: 500;
}

.timer-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  font-size: 1rem;
  color: #6b7280;
}

.timer-icon {
  font-size: 1.2rem;
}

.loading-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
}

.step {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: #f9fafb;
  transition: all 0.3s ease;
  opacity: 0.5;
}

.step.active {
  opacity: 1;
  background: #fef3f2;
  border: 1px solid #fed7d7;
}

.step.completed {
  opacity: 0.8;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.step-icon {
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

.step-text {
  font-size: 0.9rem;
  color: #374151;
  font-weight: 500;
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .html-generation-content {
    max-width: 90vw;
    padding: 2rem;
  }

  .loading-title {
    font-size: 1.5rem;
  }

  .spinner {
    width: 60px;
    height: 60px;
  }

  .html-icon {
    font-size: 1.5rem;
  }
}
</style>